import { supabase } from '@/services/supabase';
import { Database } from '@/types/database.types';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

export type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingInsert = Database['public']['Tables']['bookings']['Insert'];
type BookingUpdate = Database['public']['Tables']['bookings']['Update'];

export type Availability = Database['public']['Tables']['availabilities']['Row'];
type AvailabilityInsert = Database['public']['Tables']['availabilities']['Insert'];

export function useBookingQueries() {
    const queryClient = useQueryClient();

    // Booking fetching functions
    const fetchBookings = async (propertyId: string | string[], status: "active" | "cancelled" | null = null) => {
        if (status) {
            const { data, error } = await supabase
                .from('bookings')
                .select('*')
                .in('property_id', propertyId instanceof Array ? propertyId : [propertyId])
                .eq('status', status)
                .order('start_datetime', { ascending: true });

            if (error) throw error;
            return data as Booking[];
        }

        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .in('property_id', propertyId instanceof Array ? propertyId : [propertyId])
            .order('start_datetime', { ascending: true });

        if (error) throw error;
        return data as Booking[];
    };

    const fetchBooking = async (bookingId: string) => {
        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .eq('booking_id', bookingId)
            .single();
        if (error) throw error;
        return data as Booking;
    };

    const fetchBookingsInRange = async (propertyId: string | string[], startDate: string, endDate: string) => {
        const { data, error } = await supabase
            .from('bookings')
            .select('*')
            .in('property_id', propertyId instanceof Array ? propertyId : [propertyId])
            .gte('start_datetime', startDate)
            .lte('end_datetime', endDate)
            .order('start_datetime', { ascending: true });
        if (error) throw error;
        return data as Booking[];
    };

    // Availability fetching functions
    const fetchAvailabilities = async (propertyId: string) => {
        const { data, error } = await supabase
            .from('availabilities')
            .select('*')
            .eq('property_id', propertyId)
            .order('day_of_week', { ascending: true })
            .order('start_time', { ascending: true });
        if (error) throw error;
        return data as Availability[];
    };

    // Booking mutation functions
    const createBooking = async (bookingData: BookingInsert) => {
        const { data, error } = await supabase
            .from('bookings')
            .insert(bookingData)
            .select('*')
            .single();
        if (error) throw error;
        return data as Booking;
    };

    const updateBooking = async ({ bookingId, data }: { bookingId: string; data: BookingUpdate }) => {
        const { data: updatedData, error } = await supabase
            .from('bookings')
            .update(data)
            .eq('booking_id', bookingId)
            .select('*')
            .single();
        if (error) throw error;
        return updatedData as Booking;
    };

    const deleteBooking = async (bookingId: string) => {
        const { error } = await supabase
            .from('bookings')
            .delete()
            .eq('booking_id', bookingId);
        if (error) throw error;
        return { bookingId };
    };

    // Availability mutation functions
    const updateAvailability = async ({ propertyId, data }: { propertyId: string; data: AvailabilityInsert[] }) => {
        const { error: fetchError } = await supabase
            .from('availabilities')
            .delete()
            .eq('property_id', propertyId)
        if (fetchError) throw fetchError;

        const { data: updatedData, error } = await supabase
            .from('availabilities')
            .insert(data)
            .eq('property_id', propertyId)
            .select('*')
        if (error) throw error;

        return updatedData as Availability[];
    };

    return {
        // Booking query hooks
        useFetchBookings: (propertyId: string | string[], status: "active" | "cancelled" | null = null) => useSuspenseQuery({
            queryKey: ['bookings', propertyId instanceof Array ? propertyId.sort() : propertyId, status],
            queryFn: () => fetchBookings(propertyId, status),
        }),

        useFetchBooking: (bookingId: string) => useSuspenseQuery({
            queryKey: ['booking', bookingId],
            queryFn: () => fetchBooking(bookingId),
        }),

        useFetchBookingsInRange: (propertyId: string | string[], startDate: string, endDate: string) => useSuspenseQuery({
            queryKey: ['bookings', propertyId instanceof Array ? propertyId.sort() : propertyId, startDate, endDate],
            queryFn: () => fetchBookingsInRange(propertyId, startDate, endDate),
        }),

        // Availability query hooks
        useFetchAvailabilities: (propertyId: string) => useSuspenseQuery({
            queryKey: ['availabilities', propertyId],
            queryFn: () => fetchAvailabilities(propertyId),
        }),

        // Booking mutation hooks
        createBookingMutation: useMutation({
            mutationFn: createBooking,
            onSuccess: (data) => {
                queryClient.setQueryData(['booking', data.booking_id], data);
                queryClient.invalidateQueries({ queryKey: ['bookings', data.property_id] });
            }
        }),

        updateBookingMutation: useMutation({
            mutationFn: updateBooking,
            onSuccess: (data) => {
                queryClient.setQueryData(['booking', data.booking_id], data);
                queryClient.invalidateQueries({ queryKey: ['bookings'] });
            }
        }),

        deleteBookingMutation: useMutation({
            mutationFn: deleteBooking,
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['bookings'] });
            }
        }),

        // Availability mutation hooks
        updateAvailabilitiesMutation: useMutation({
            mutationFn: updateAvailability,
            onSuccess: (data) => {
                queryClient.invalidateQueries({ queryKey: ['availabilities', data[0]?.property_id] });
            }
        }),
    };
}